import React from 'react'
import { Field as Input, FormSection } from 'redux-form'
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import _ from 'lodash'

import {
    CheckboxInput,
    DateInput,
    TechnicalDataInput,
    CertificationSelectInput,
    DocumentSelectInput,
    StringInput,
    NumberInput,
    EnumSelectInput
} from '../Inputs'
import { schemaAsFieldArray } from '../../utils/schemas'


const Certification = ({ fieldName, disabled }) => {
    return (
        <Input
            key={`ds-ESTADO_CERTIFICACION-${fieldName}`}
            name={`ESTADO_CERTIFICACION.${fieldName}`}
            required={false}
            component={CertificationSelectInput}
            disabled={disabled}
        />
    )
}

const renderInput = (
    estadosCertificacion,
    disableDatasheetFields,
    disableValidatedFields,
    disableCertificationFields,
    disableDocumentUploadFields
) => (field) => {
    field.validado = ["Validado", "En Uso"].includes(_.get(estadosCertificacion, field.name, 'No Revisado'))
    field.disabled = disableDatasheetFields || (disableValidatedFields && field.validado)
    field.disableCertification = disableCertificationFields
    switch (field.type) {
        case "string":
            return renderStringInput(field)
        case "number":
            return renderNumberInput(field)
        case "boolean":
            return renderBooleanInput(field)
        case "reference":
            return renderReferenceInput(field)
        case "technicalData":
            return renderTechnicalDataInput(field)
        case "date":
            return renderDateInput(field)
        case "document":
            field.disabled = field.disabled || disableDocumentUploadFields
            return renderDocumentInput(field)
        default:
            return null
    }
}


const renderStringInput = (field) => {
    const { name, title, certification, disableCertification, required, disabled } = field

    const input = !!field.enum
        ? (
            <Input
                key={`ds-${name}`}
                name={name}
                required={false}
                component={EnumSelectInput}
                choices={field.enum}
                disabled={disabled}
            />
        )
        : <StringInput field={field}/>

    return (
        <TableRow key={`ds-row-${name}`}>
            <TableCell align="center">{title}{required ? '*' : null}</TableCell>
            <TableCell align="center">{input}</TableCell>
            <TableCell align="center">
                {!!certification && (
                    <Certification fieldName={name} disabled={disableCertification}/>
                )}
            </TableCell>
        </TableRow>
    )
}

const renderNumberInput = (field) => {
    const { name, title, certification, disableCertification, required } = field
    const input = <NumberInput field={field} />

    return (
        <TableRow key={`ds-row-${name}`}>
            <TableCell align="center">{title}{required ? '*' : null}</TableCell>
            <TableCell align="center">{input}</TableCell>
            <TableCell align="center">
                {!!certification && (
                    <Certification fieldName={name} disabled={disableCertification}/>
                )}
            </TableCell>
        </TableRow>
    )
}

const renderBooleanInput = (field) => {
    const { name, title, required, certification, disabled, disableCertification } = field

    const input = (
        <Input
            key={`ds-${name}`}
            name={name}
            required={false}
            component={CheckboxInput}
            disabled={disabled}
        />
    )

    return (
        <TableRow key={`ds-row-${name}`}>
            <TableCell align="center">{title}{required ? '*' : null}</TableCell>
            <TableCell align="center">{input}</TableCell>
            <TableCell align="center">
                {!!certification && (
                    <Certification fieldName={name} disabled={disableCertification}/>
                )}
            </TableCell>
        </TableRow>
    )
}

const renderReferenceInput = (field) => {
    const { name, fields, certification, disabled, disableCertification, required } = field
    return (
        <TableRow>
            <TableCell align="center">{field.title}{required ? '*' : null}</TableCell>
            <TableCell align="center">
                <FormSection key={`section-${name}`} name={name} component={Table}>
                    <TableBody>
                        {_.map(fields, renderInput({}, disabled, disabled, disableCertification))}
                    </TableBody>
                </FormSection>
            </TableCell>
            <TableCell align="center">
                {!!certification && (
                    <Certification fieldName={name} disabled={disableCertification}/>
                )}
            </TableCell>
        </TableRow>

    )
}

const renderTechnicalDataInput = (field) => {
    const { name, title, required, choicesSchema : schemaId, certification, disabled, disableCertification } = field

    const input = (
        <Input
            key={`ds-${name}`}
            name={name}
            required={false}
            component={TechnicalDataInput}
            schemaId={schemaId}
            disabled={disabled}
        />
    )

    return (
        <TableRow key={`ds-row-${name}`}>
            <TableCell align="center">{title}{required ? '*' : null}</TableCell>
            <TableCell align="center">{input}</TableCell>
            <TableCell align="center">
                {!!certification && (
                    <Certification fieldName={name} disabled={disableCertification}/>
                )}
            </TableCell>
        </TableRow>
    )
}

const renderDateInput = (field) => {
    const { name, title, required, certification, disabled, disableCertification } = field

    const input = (
        <Input
            key={`ds-${name}`}
            name={name}
            required={false}
            component={DateInput}
            disabled={disabled}
        />
    )

    return (
        <TableRow key={`ds-row-${name}`}>
            <TableCell align="center">{title}{required ? '*' : null}</TableCell>
            <TableCell align="center">{input}</TableCell>
            <TableCell align="center">
                {!!certification && (
                    <Certification fieldName={name} disabled={disableCertification}/>
                )}
            </TableCell>
        </TableRow>
    )
}

const renderDocumentInput = (field) => {
    const { name, title, required, fileType, extensions, certification, disabled, disableCertification } = field

    const input = (
        <Input
            key={`ds-${name}`}
            name={name}
            required={false}
            component={DocumentSelectInput}
            documentType={fileType}
            extensions={extensions}
            disabled={disabled}
        />
    )

    return (
        <TableRow key={`ds-row-${name}`}>
            <TableCell align="center">{title}{required ? '*' : null}</TableCell>
            <TableCell align="center">{input}</TableCell>
            <TableCell align="center">
                {!!certification && (
                    <Certification fieldName={name} disabled={disableCertification}/>
                )}
            </TableCell>
        </TableRow>
    )
}

export const renderDatasheetInputs = (
    schema,
    estadoCertificacion,
    disableDatasheetFields,
    disableVerifiedDatasheetFields,
    disableCertificationFields,
    disableDocumentUploadFields
) => {
    const fields = schemaAsFieldArray(schema)
    return _.map(
        fields,
        renderInput(
            estadoCertificacion,
            disableDatasheetFields,
            disableVerifiedDatasheetFields,
            disableCertificationFields,
            disableDocumentUploadFields
        )
    )
}
